// src/hooks/useDelayReasons.ts
//
// EE-216 - the delay reasons, fetched once for the whole app.
//
// One list answers all three "why": the last QC step closing after the
// Production Deadline, an order completing after its delivery date, and a
// Production Deadline being moved. Records carry the reason as a code
// (MATERIAL_DEPENDENCY); the labels live in the backend's DELAY_REASONS table
// and arrive over GET /v1/ecommerce/productions/delay-reasons. Every row of a
// table may need that mapping, so this is one cached request, not one per row.
//
// Do not add a label map here. The whole point of the table is that the list
// is edited without a deploy, and a copy in TypeScript would be wrong the
// first time someone renames a reason - the order-completion round shipped
// one and it went stale.
import { useContext } from "react";
import { useQuery } from "react-query";
import AppStateContext from "contexts/AppStateContext";
import { DelayReasonOptionType } from "types/ProductionType";
import { apiGetDelayReasons } from "util/network/Productions";

export const DELAY_REASONS_QUERY_KEY = ["delayReasons"];

/**
 * The reason options, plus a code -> label resolver.
 *
 * `options` is empty until the request lands. `labelFor` falls back to the
 * code, so a row still says something truthful when the list is missing or
 * has grown a value this build has never seen.
 *
 * Cached for an hour: a setup table is edited rarely, and a stale label for
 * an hour is cheaper than a request per rendered row.
 */
export const useDelayReasons = (): {
  options: DelayReasonOptionType[];
  selectableOptions: DelayReasonOptionType[];
  isLoading: boolean;
  isUnavailable: boolean;
  labelFor: (code: string) => string;
} => {
  const { token } = useContext(AppStateContext);
  const { data, isLoading, isError } = useQuery<DelayReasonOptionType[]>(
    DELAY_REASONS_QUERY_KEY,
    () => apiGetDelayReasons(token),
    { enabled: !!token, staleTime: 60 * 60 * 1000 }
  );
  const options = data ?? [];
  // Retired reasons are served so their labels survive on the records that
  // used them, but nothing new may be filed under one.
  const selectableOptions = options.filter((option) => option.isActive);
  return {
    options,
    selectableOptions,
    isLoading,
    // A failed request and an empty table look the same to the user and have
    // the same consequence: no reason can be picked, so nothing that needs
    // one can be saved. The dialogs say so rather than showing an empty
    // dropdown.
    isUnavailable: isError || (!isLoading && selectableOptions.length === 0),
    labelFor: (code: string) =>
      options.find((option) => option.code === code)?.label ?? code,
  };
};
